import { useState } from 'react';
import type { AxiosInstance } from 'axios';
import { useApi } from '@/api/axios';
import { pleasService } from '../services/pleasService';
import { formatDateTimeLocal } from '../utils/formatters';
import { isPleaDecidedConflict } from '../utils/errors';
import { usePleaAttachments } from './usePleaAttachments';
import type { Plea, PleaFormData } from '../types';

const globalWithNotify = globalThis as typeof globalThis & {
  pushNotification?: (type: string, message: string) => void;
};

function triggerPushNotification(type: string, message: string): void {
  globalWithNotify.pushNotification?.(type, message);
}

export const PLEA_DECIDED_UPDATE_MESSAGE = 'Este pedido já foi analisado pelo gestor e não pode mais ser alterado.';
export const PLEA_DECIDED_DELETE_MESSAGE = 'Este pedido já foi analisado pelo gestor e não pode mais ser excluído.';
export const PLEA_DECIDED_ATTACHMENT_MESSAGE =
  'Pedido atualizado, mas o gestor já o analisou e o anexo não pôde ser enviado.';

const EMPTY_FORM: PleaFormData = {
  type: '',
  description: '',
  start: '',
  startTime: '',
  end: '',
  endTime: '',
  file: null,
};

function isEsquecimento(type: string): boolean {
  return type === 'Esquecimento';
}

function mergeDateTime(date: string, time: string): string {
  if (!date) return '';
  return time ? `${date}T${time}` : date;
}

async function uploadAttachment(
  api: AxiosInstance,
  agreementUuid: string,
  pleaUuid: string,
  file: File,
): Promise<void> {
  const { url, path } = await pleasService.requestAttachmentUploadUrl(api, agreementUuid, pleaUuid, {
    originalFilename: file.name,
    contentType: file.type,
    sizeBytes: file.size,
  });
  await pleasService.uploadToSignedUrl(url, file.type, file);
  await pleasService.confirmAttachment(api, agreementUuid, pleaUuid, {
    path,
    originalFilename: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
  });
}

interface UsePleaFormOptions {
  agreementUuid: string | undefined;
  onSuccess: () => void;
  onError: (msg: string) => void;
  onPleaDecided: (msg: string) => void;
}

/**
 * Drives the add, edit and delete flows of the pleas page, including attachment upload.
 * A 409 Conflict (plea already decided by the manager) closes the open flow and is reported through onPleaDecided.
 */
export function usePleaForm({ agreementUuid, onSuccess, onError, onPleaDecided }: UsePleaFormOptions) {
  const api = useApi();

  const notifyPleaDecided = (message: string) => {
    onPleaDecided(message);
    triggerPushNotification('warning', message);
  };

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<PleaFormData>(EMPTY_FORM);

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState('');
  const [editForm, setEditForm] = useState<PleaFormData>(EMPTY_FORM);

  const attachments = usePleaAttachments(agreementUuid);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);

  const openAdd = () => setAddOpen(true);
  const closeAdd = () => setAddOpen(false);

  const handleAdd = async () => {
    if (!agreementUuid) return;
    const currentFile = form.file;
    setSubmitting(true);
    try {
      const payload = {
        type: form.type,
        description: form.description,
        start: formatDateTimeLocal(mergeDateTime(form.start, form.startTime)),
        ...(!isEsquecimento(form.type) && form.end
          ? { end: formatDateTimeLocal(mergeDateTime(form.end, form.endTime)) }
          : {}),
      };
      const plea = await pleasService.createPlea(api, agreementUuid, payload);
      setAddOpen(false);
      setForm(EMPTY_FORM);
      onSuccess();

      if (currentFile) {
        const pleaUuid = plea.self.split('/').pop()!;
        try {
          await uploadAttachment(api, agreementUuid, pleaUuid, currentFile);
          onSuccess();
        } catch {
          onError('Pedido criado, mas o anexo falhou. Tente anexar de novo.');
          triggerPushNotification('error', 'Pedido criado, mas o anexo falhou. Tente anexar de novo.');
        }
      }
    } catch {
      onError('Erro ao adicionar pedido');
      triggerPushNotification('error', 'Erro ao adicionar pedido');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (plea: Plea) => {
    const pleaUuid = plea.self.split('/').pop() ?? '';
    setEditId(pleaUuid);
    setEditForm({
      type: plea.type,
      description: plea.description,
      start: plea.start.substring(0, 10),
      startTime: plea.start.length > 10 ? plea.start.substring(11, 16) : '',
      end: plea.end ? plea.end.substring(0, 10) : '',
      endTime: plea.end && plea.end.length > 10 ? plea.end.substring(11, 16) : '',
      file: null,
    });
    setEditOpen(true);
    attachments.load(plea);
  };
  const closeEdit = () => {
    setEditOpen(false);
    attachments.reset();
  };

  const handleEditSubmit = async () => {
    if (!agreementUuid) return;
    const currentFile = editForm.file;
    setSubmitting(true);
    try {
      const payload = {
        type: editForm.type,
        description: editForm.description,
        start: formatDateTimeLocal(mergeDateTime(editForm.start, editForm.startTime)),
        ...(!isEsquecimento(editForm.type) && editForm.end
          ? { end: formatDateTimeLocal(mergeDateTime(editForm.end, editForm.endTime)) }
          : {}),
      };
      await pleasService.updatePlea(api, agreementUuid, editId, payload);
      setEditOpen(false);
      onSuccess();

      if (currentFile) {
        try {
          await uploadAttachment(api, agreementUuid, editId, currentFile);
          onSuccess();
        } catch (error) {
          if (isPleaDecidedConflict(error)) {
            notifyPleaDecided(PLEA_DECIDED_ATTACHMENT_MESSAGE);
            return;
          }
          onError('Pedido atualizado, mas o anexo falhou. Tente anexar de novo.');
          triggerPushNotification('error', 'Pedido atualizado, mas o anexo falhou. Tente anexar de novo.');
        }
      }
    } catch (error) {
      if (isPleaDecidedConflict(error)) {
        closeEdit();
        notifyPleaDecided(PLEA_DECIDED_UPDATE_MESSAGE);
        return;
      }
      onError('Erro ao atualizar pedido');
      triggerPushNotification('error', 'Erro ao atualizar pedido');
    } finally {
      setSubmitting(false);
    }
  };

  const openDelete = (id: string) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };
  const closeDelete = () => {
    setDeleteOpen(false);
    setDeleteId(null);
  };

  const handleDelete = async () => {
    if (!agreementUuid || !deleteId) return;
    setSubmitting(true);
    try {
      await pleasService.deletePlea(api, agreementUuid, deleteId);
      setDeleteOpen(false);
      setDeleteId(null);
      onSuccess();
    } catch (error) {
      if (isPleaDecidedConflict(error)) {
        closeDelete();
        notifyPleaDecided(PLEA_DECIDED_DELETE_MESSAGE);
        return;
      }
      onError('Erro ao excluir pedido');
      triggerPushNotification('error', 'Erro ao excluir pedido');
    } finally {
      setSubmitting(false);
    }
  };

  return {
    addOpen,
    form,
    setForm,
    editOpen,
    editForm,
    setEditForm,
    attachments: attachments.attachments,
    attachmentsLoading: attachments.attachmentsLoading,
    attachmentsError: attachments.attachmentsError,
    deleteOpen,
    submitting,
    openAdd,
    closeAdd,
    handleAdd,
    openEdit,
    closeEdit,
    handleEditSubmit,
    openDelete,
    closeDelete,
    handleDelete,
  };
}
